import { gql } from '@apollo/client';
import { FR_ASSET_FIELDS } from './asset';
import { FR_ISSUE_STATUS_FIELDS } from './issue';
import { FR_SERVICE_REASON_FIELDS } from './serviceReason';
import { FR_ASSEMBLY_FIELDS } from './assembly';
import { FR_COMPONENT_FIELDS } from './component';
import { FR_CATEGORY_FIELDS } from './category';

export const FR_SERVICE_WORK_FIELDS = gql`
    fragment fr_ServiceWorkFields on ServiceWork {
        ServiceWorkId
        Name
        sv_Name: Name(LangCode: SV)
        en_Name: Name(LangCode: EN)
        no_Name: Name(LangCode: NO)
        Description
        sv_Description: Description(LangCode: SV)
        en_Description: Description(LangCode: EN)
        no_Description: Description(LangCode: NO)
        Created
        Updated
    }
`;

export const FR_SERVICE_TASK_FIELDS = gql`
    fragment fr_ServiceTaskFields on ServiceTask {
        ServiceTaskId
        Name
        ServiceItem {
            ServiceItemId
            Category {
                CategoryId
                Name
                sv_Name: Name(LangCode: SV)
                en_Name: Name(LangCode: EN)
                no_Name: Name(LangCode: NO)
            }
            ServiceWork {
                Name
                sv_Name: Name(LangCode: SV)
                en_Name: Name(LangCode: EN)
                no_Name: Name(LangCode: NO)
                ServiceWorkId
            }
            Assembly {
                AssemblyId
                Name
                sv_Name: Name(LangCode: SV)
                en_Name: Name(LangCode: EN)
                no_Name: Name(LangCode: NO)
            }
            Component {
                ComponentId
                Name
                sv_Name: Name(LangCode: SV)
                en_Name: Name(LangCode: EN)
                no_Name: Name(LangCode: NO)
            }
        }
        sv_Name: Name(LangCode: SV)
        en_Name: Name(LangCode: EN)
        no_Name: Name(LangCode: NO)
        Description
        sv_Description: Description(LangCode: SV)
        en_Description: Description(LangCode: EN)
        no_Description: Description(LangCode: NO)
        Created
        Updated
        Access {
            Read
            Create
            Delete
            Update
        }
        OrganizationAccess {
            Organization {
                OrganizationId
                Name
            }
            OrganizationAccessType {
                __typename
                ... on ServiceTaskOrganizationType {
                    ServiceTaskOrganizationTypeId
                    Name
                }
            }
        }
    }
`;

export const FR_SERVICE_WORK_FIELDS_WITH_TASK = gql`
    fragment fr_ServiceWorkFieldsWithTask on ServiceWork {
        ServiceWorkId
        Name
        sv_Name: Name(LangCode: SV)
        en_Name: Name(LangCode: EN)
        no_Name: Name(LangCode: NO)
        Description
        sv_Description: Description(LangCode: SV)
        en_Description: Description(LangCode: EN)
        no_Description: Description(LangCode: NO)
        Created
        Updated
        ServiceTask {
            ...fr_ServiceTaskFields
        }
    }
    ${FR_SERVICE_TASK_FIELDS}
`;

export const FR_SERVICE_ITEM_FIELDS = gql`
    fragment fr_ServiceItemFields on ServiceItem {
        ServiceItemId
        Order
        ServiceTask {
            ...fr_ServiceTaskFields
        }
        ServiceWork {
            ...fr_ServiceWorkFields
        }
        Access {
            Read
            Create
            Delete
            Update
        }
    }
    ${FR_SERVICE_TASK_FIELDS}
    ${FR_SERVICE_WORK_FIELDS}
`;

export const FR_SERVICE_PRIORITY_CLASS_FIELDS = gql`
    fragment fr_ServicePriorityClassFields on ServicePriorityClass {
        ServicePriorityClassId
        Name
    }
`;

export const FR_SERVICE_RECURRENT_METER_TYPE = gql`
    fragment fr_ServiceRecurrentMeterTypeFields on RecurrentMeterType {
        RecurrentMeterTypeId
        Name
    }
`;

export const FR_SERVICE_RECURRENT_TYPE = gql`
    fragment fr_ServiceRecurrentTypeFields on RecurrentType {
        RecurrentTypeId
        Name
    }
`;

export const FR_SERVICE_SCHEDULE = gql`
    fragment fr_ServiceScheduleFields on Service {
        ServiceScheduleId
        Name
        sv_Name: Name(LangCode: SV)
        en_Name: Name(LangCode: EN)
        no_Name: Name(LangCode: NO)
        Description
        sv_Description: Description(LangCode: SV)
        en_Description: Description(LangCode: EN)
        no_Description: Description(LangCode: NO)
        ServiceItems {
            ServiceItemId
            Order
            ServiceTask {
                ServiceTaskId
                Name
                sv_Name: Name(LangCode: SV)
                en_Name: Name(LangCode: EN)
                no_Name: Name(LangCode: NO)
            }
            Category {
                CategoryId
                Name
                sv_Name: Name(LangCode: SV)
                en_Name: Name(LangCode: EN)
                no_Name: Name(LangCode: NO)
            }
            ServiceWork {
                ServiceWorkId
                Name
                sv_Name: Name(LangCode: SV)
                en_Name: Name(LangCode: EN)
                no_Name: Name(LangCode: NO)
            }
            Assembly {
                AssemblyId
                Name
                sv_Name: Name(LangCode: SV)
                en_Name: Name(LangCode: EN)
                no_Name: Name(LangCode: NO)
            }
            Component {
                ComponentId
                Name
                sv_Name: Name(LangCode: SV)
                en_Name: Name(LangCode: EN)
                no_Name: Name(LangCode: NO)
            }
        }
        ServiceEntry {
            ServiceEntryId
            ServiceEntryTask {
                ServiceEntryTaskId
            }
        }
        ServiceProgram {
            ServiceProgramId
            RecurrentMeterType {
                RecurrentMeterTypeId
                Name
            }
            SecondaryRecurrentMeterType {
                RecurrentMeterTypeId
                Name
            }
        }
        RecurrentCycle
        SecondaryRecurrentCycle
        RecurrentType {
            ...fr_ServiceRecurrentTypeFields
        }
        SecondaryRecurrentType {
            ...fr_ServiceRecurrentTypeFields
        }
        SecondaryRecurrentMeterType {
            ...fr_ServiceRecurrentMeterTypeFields
        }
        RecurrentMeterType {
            ...fr_ServiceRecurrentMeterTypeFields
        }
        Created
        Updated
        Access {
            Read
            Create
            Delete
            Update
        }
        OrganizationAccess {
            Organization {
                OrganizationId
                Name
            }
            OrganizationAccessType {
                __typename
                ... on ServiceScheduleOrganizationType {
                    ServiceScheduleOrganizationTypeId
                    Name
                }
            }
        }
    }
    ${FR_SERVICE_RECURRENT_TYPE}
    ${FR_SERVICE_RECURRENT_METER_TYPE}
`;

export const FR_SERVICE_ISSUE_FIELDS = gql`
    fragment fr_ServiceIssueFields on Issue {
        IssueId
        Description
        Summary
        x
        y
        AssetName
        AssetType
        IssueStatus {
            ...fr_IssueStatusFields
        }
        Asset {
            ...fr_AssetFields
        }
        IssueUser {
            IssueUserId
            IssueUserType {
                IssueUserTypeId
                Name
            }
            User {
                UserId
                Firstname
                Lastname
            }
            Created
        }
        Access {
            Read
            Create
            Delete
            Update
        }
        EquipmentIssueCount
        Created
        Updated
        Resolved
        ServiceEntry {
            ServiceEntryId
            Name
        }
        Note
        IssueSource {
            IssueSourceId
            Name
        }
        Due
        IssuePriority {
            IssuePriorityId
            Name
        }
        ServiceWork {
            Description
            Name
            ServiceTask {
                IsResolvingIssue
                Name
                ServiceTaskId
            }
            ServiceWorkId
        }
    }
    ${FR_ASSET_FIELDS}
    ${FR_ISSUE_STATUS_FIELDS}
`;

export const FR_SERVICE_ENTRY_FIELDS = gql`
    fragment fr_ServiceEntryFields on ServiceEntry {
        ServiceEntryId
        Name
        Description
        ServiceDate
        OperatingHours
        ActualCost
        EstimateCost
        OrderNbr
        DesiredServiceDate
        CustomerReferenceNumber
        Access {
            Read
            Update
            Delete
            Create
        }
        ServicePriorityClass {
            ...fr_ServicePriorityClassFields
        }
        RecurrentMeterType {
            ...fr_ServiceRecurrentMeterTypeFields
        }
        ServiceEntryStatus {
            Name
        }
        ServiceEntryTask {
            ServiceEntryTaskId
            ServiceTime
            Description
            ActualCost
            EstimateCost
            ServicePerson {
                UserId
                Firstname
                Lastname
            }
            ServicePart {
                ServicePartId
                PartNumber
                Amount
                Category {
                    ...fr_CategoryFields
                }
                Component {
                    ...fr_ComponentFields
                }
                Assembly {
                    ...fr_AssemblyFields
                }
                ServiceWork {
                    ...fr_ServiceWorkFields
                }
                ServiceReason {
                    ...fr_ServiceReasonFields
                }
            }
            ServiceItem {
                ServiceItemId
                Order
                Category {
                    ...fr_CategoryFields
                }
                ServiceWork {
                    ...fr_ServiceWorkFields
                }
                Assembly {
                    ...fr_AssemblyFields
                }
                Component {
                    ...fr_ComponentFields
                }
                ServiceTask {
                    ...fr_ServiceTaskFields
                }
            }
            ServiceEntryTaskStatus {
                Name
            }
            Issue {
                ...fr_ServiceIssueFields
            }
        }
        ServiceEntryOrderer {
            Firstname
            Lastname
            UserId
        }
        ServiceEntryCompleter {
            Firstname
            Lastname
            UserId
        }
        Completed
        CompletedDate
        ServiceSchedule {
            ServiceScheduleId
            ServiceItems {
                ServiceItemId
                ServiceTask {
                    ServiceTaskId
                }
                ServiceWork {
                    ServiceWorkId
                }
            }
        }
        ServiceVendor {
            OrganizationId
            Name
        }
        Issues {
            ...fr_ServiceIssueFields
        }
        Machine {
            MachineId
            Name
            SerialNbr
            Organization {
                OrganizationId
                Name
            }
            MachineType {
                Name
                MachineTypeId
            }
            Asset {
                AssetName
                AssetType
            }
        }
        Created
        Updated
    }
    ${FR_SERVICE_WORK_FIELDS}
    ${FR_SERVICE_TASK_FIELDS}
    ${FR_SERVICE_PRIORITY_CLASS_FIELDS}
    ${FR_SERVICE_RECURRENT_METER_TYPE}
    ${FR_SERVICE_ISSUE_FIELDS}
    ${FR_SERVICE_REASON_FIELDS}
    ${FR_ASSEMBLY_FIELDS}
    ${FR_COMPONENT_FIELDS}
    ${FR_ASSEMBLY_FIELDS}
    ${FR_CATEGORY_FIELDS}
    ${FR_COMPONENT_FIELDS}
    ${FR_CATEGORY_FIELDS}
`;

export const FR_SERVICE_AGREEMENT_FIELDS = gql`
    fragment fr_ServiceAgreementFields on ServiceAgreement {
        ServiceAgreementId
        StartDate
        EndDate
        Machine {
            __typename
            MachineId
            Name
            SerialNbr
            MachineType {
                Name
                MachineTypeId
            }
        }
        ServiceProgram {
            ServiceProgramId
            Name
            no_Name: Name(LangCode: NO)
            sv_Name: Name(LangCode: SV)
            en_Name: Name(LangCode: EN)
        }
    }
`;

export const FR_SERVICE_PROGRAM_FIELDS = gql`
    fragment fr_ServiceProgramFields on ServiceProgram {
        ServiceProgramId
        Name
        sv_Name: Name(LangCode: SV)
        en_Name: Name(LangCode: EN)
        no_Name: Name(LangCode: NO)
        Description
        sv_Description: Description(LangCode: SV)
        en_Description: Description(LangCode: EN)
        no_Description: Description(LangCode: NO)
        ServiceSchedule {
            ServiceScheduleId
            Name
            sv_Name: Name(LangCode: SV)
            en_Name: Name(LangCode: EN)
            no_Name: Name(LangCode: NO)
            ServiceItems {
                ServiceItemId
            }
        }
        ServiceAgreement {
            ...fr_ServiceAgreementFields
        }
        RecurrentMeterType {
            ...fr_ServiceRecurrentMeterTypeFields
        }
        SecondaryRecurrentMeterType {
            ...fr_ServiceRecurrentMeterTypeFields
        }
        Created
        Updated
        Access {
            Read
            Create
            Delete
            Update
        }
        OrganizationAccess {
            Organization {
                OrganizationId
                Name
            }
            OrganizationAccessType {
                __typename
                ... on ServiceProgramOrganizationType {
                    ServiceProgramOrganizationTypeId
                    Name
                }
            }
        }
    }
    ${FR_SERVICE_AGREEMENT_FIELDS}
    ${FR_SERVICE_RECURRENT_METER_TYPE}
`;

